// js/sections/RecentlyViewedProductsSection.js
import { SecureStorage } from 'utils';
import { shopifySectionsAPI } from 'shopify-sections-api';

class RecentlyViewedProductsSection extends HTMLElement {
  connectedCallback() {
    this.settings = JSON.parse(this.getAttribute("data-settings")) || {};
    this.settings["ignoreIds"] = this.settings["ignoreIds"] || [];

    // Inside the cart drawer (lazy) the strip is off-screen on every page load.
    // Defer the fetch until it first scrolls into view (drawer opens) so we
    // don't re-render the whole drawer on pages where the cart is never opened.
    if (this.settings["lazy"]) {
      this._observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this._observer.disconnect();
          this._observer = null;
          this._fetchProducts();
        }
      });
      this._observer.observe(this);
    } else {
      this._fetchProducts();
    }
  }
  disconnectedCallback() {
    this._observer?.disconnect();
    // Slider cleanup is handled by cos-slider component
  }
  /**
   * In order to get the products to display, we hit the search template with the given IDs.
   */
  async _fetchProducts() {
    const queryString = this._getSearchQueryString();
    
    if (queryString === "" || this.getAttribute("initialized")) {
      this._handleHeadingVisibility(false);
      this._handleSectionVisibility(false);
      return;
    } 
    
    // Prevent multiple simultaneous calls
    if (this._isLoading) {
      return;
    }
    this._isLoading = true;
    
    this._handleHeadingVisibility(true);
    
    try {
      // Use the unified fetchProducts method with search API
      const productIds = this._getProductIds();
      
      // Fetch via THIS section's own id. The Section Rendering API renders it
      // on /search (cross-template section_id resolves fine), so the snippet's
      // SSR loop runs and emits product-item cards with the slider grid_classes
      // (1/6--desk) — visually matching the recommendations carousel. Fetching
      // via 'main-search' instead would inherit the wide search-grid widths.
      const products = await shopifySectionsAPI.fetchProducts(productIds, this.settings["sectionId"] || 'main-search');
      
      const productsContainer = this.querySelector('[data-products-grid]');
      
      if (products && products.length > 0 && productsContainer) {
        // Use the shared rendering utility
        shopifySectionsAPI.renderProducts(products, productsContainer, {
          emptyMessage: ''
        });
        
        this.setAttribute("initialized", "true");
        this._handleSectionVisibility(true);
        
        // Reinitialize slider after products are loaded
        requestAnimationFrame(() => {
          const slider = this.querySelector('cos-slider');
          
          if (slider && typeof slider.reinitialize === 'function') {
            slider.reinitialize();
          }
        });
      } else {
        this._handleSectionVisibility(false);
      }
    } catch (error) {
      this._handleSectionVisibility(false);
    } finally {
      this._isLoading = false;
    }
  }
  _getProductIds() {
    const items = SecureStorage.getJSON("recentlyViewedProducts", []);

    // ignoreIds arrives string-typed (Liquid `| split: ','`) while stored ids
    // are numbers — coerce both to Number so the current product is actually
    // excluded from its own strip.
    const ignore = (this.settings["ignoreIds"] || []).map((id) => Number(id));

    return items.filter((item) => !ignore.includes(Number(item)));
  }

  _getSearchQueryString() {
    return this._getProductIds().map((item) => "id:" + item).join(" OR ");
  }
  _handleHeadingVisibility(visible) {
    const heading = this.parentElement.querySelector(".section__title");
    if (!heading) return;

    heading.style.display = visible ? "block" : "none";
  }
  _handleSectionVisibility(visible) {
    const section = this.closest('[data-section-type="recently-viewed-products"]');
    if (!section) return;

    section.style.display = visible ? "block" : "none";
  }
};
if (!window.customElements.get("cos-recently-viewed-products")) window.customElements.define("cos-recently-viewed-products", RecentlyViewedProductsSection);
